import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import ModalContext from '../../contexts/ModalContext';
import favoritesImage from '../../assets/images/favorieten.png';
import WordlistContext from './_contexts/WordlistContext';
import WordlistViewModeContext, { WordlistViewMode } from './_contexts/WordlistViewModeContext';
import useWordlistWords from './_hooks/useWordlistWords';
import WordlistHierarchyList from './WordlistHierarchyList';
import { buildGroupProps } from '../../utils/elementisUtils';

import { Tabpanel } from '@pelckmans/business-components/components/tabs';
import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import TrashIcon from '@pelckmans/elementis/icons/trash';

const MODAL_ID = 'confirm-clear-favorites-modal';

type ConfirmClearFavoritesModalProps = {
  id: string;
  onConfirm: () => void;
};

const ConfirmClearFavoritesModal = ({ id, onConfirm }: ConfirmClearFavoritesModalProps) => {
  const { t } = useTranslation();
  const { closeModal } = useContext(ModalContext);

  const handleClose = () => closeModal(id, true);

  const handleConfirm = () => {
    onConfirm();
    handleClose();
  };

  return (
    <Dialog
      id={id}
      modal
      size="sm"
      onClose={handleClose}
      aria-label={t('MODULES.MATERIAL.COMPONENTS.WORD_LISTS.CLEAR_MY_WORDLIST_DIALOG.TITLE')}
    >
      <Dialog.Header
        title={t('MODULES.MATERIAL.COMPONENTS.WORD_LISTS.CLEAR_MY_WORDLIST_DIALOG.TITLE')}
        onClose={handleClose}
        aria-label={t('MODULES.MATERIAL.COMPONENTS.WORD_LISTS.CLEAR_MY_WORDLIST_DIALOG.TITLE')}
        danger
      />
      <Dialog.Body>{t('MODULES.MATERIAL.COMPONENTS.WORD_LISTS.CLEAR_MY_WORDLIST_DIALOG.MESSAGE')}</Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsRight={buildGroupProps(
          <>
            <Button key="no" prominence="neutral" onClick={handleClose}>
              {t('ACTIONS.NO')}
            </Button>
            <Button key="yes" prominence="danger" onClick={handleConfirm}>
              {t('ACTIONS.YES')}
            </Button>
          </>,
        )}
      />
    </Dialog>
  );
};

export default function WordlistFavoritesTab() {
  const { t } = useTranslation();
  const words = useWordlistWords();

  const { clearFavoriteWords } = useContext(WordlistContext);
  const { setViewMode } = useContext(WordlistViewModeContext);
  const { showModal } = useContext(ModalContext);

  return (
    <>
      <Tabpanel tabId="favorites" className="wordlist__tab">
        {(words || []).length > 0 ? (
          <>
            <header className="wordlist__header wordlist__header--level-0 wordlist__header--favorites">
              {t('MODULES.MATERIAL.COMPONENTS.WORD_LISTS.FAVORITE_TITLE')}
              <Button
                prominence="neutral"
                size="sm"
                onClick={() => showModal(<ConfirmClearFavoritesModal id={MODAL_ID} onConfirm={clearFavoriteWords} />)}
                startIcon={<TrashIcon />}
                aria-label={t('MODULES.MATERIAL.COMPONENTS.WORD_LISTS.CLEAR_MY_WORDLIST_DIALOG.TITLE')}
              />
            </header>

            <WordlistHierarchyList />
            <div className="wordlist__actions">
              <Button prominence="neutral" onClick={() => setViewMode(WordlistViewMode.FOCUS)}>
                {t('MODULES.MATERIAL.COMPONENTS.WORD_LISTS.LEARN_WORDS')}
              </Button>
            </div>
          </>
        ) : (
          <div className="wordlist__no-favorites">
            {t('MODULES.MATERIAL.COMPONENTS.WORD_LISTS.NO_FAVORITES_SELECTED')}
            <img src={favoritesImage} alt={t('MODULES.MATERIAL.COMPONENTS.WORD_LISTS.FAVORITE_TITLE')} />
          </div>
        )}
      </Tabpanel>
    </>
  );
}
